import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '../supabaseClient';

const GameContext = createContext();

const DEFAULT_PLAYER = { hp: 0, xp: 0, level: 1, xpToLevelUp: 5, lastChecked: new Date().toISOString() };

const DEFAULT_CATEGORIES = [
  { id: 'faith', name: 'Faith', icon: 'F', color: '#00ff88' },
  { id: 'family', name: 'Family', icon: 'Fa', color: '#00e5ff' },
  { id: 'finances', name: 'Finances', icon: '$', color: '#ffd700' },
  { id: 'formation', name: 'Formation', icon: 'Ed', color: '#ff6b35' },
  { id: 'fitness', name: 'Fitness', icon: 'Ft', color: '#ff4081' },
  { id: 'fun', name: 'Fun', icon: 'G', color: '#b388ff' }
];

const DEFAULT_TOOL_CATEGORIES = [
  { id: 'meetings', name: 'Meetings' },
  { id: 'ai', name: 'AI Chatbots' },
  { id: 'notes', name: 'Note Taking' },
  { id: 'focus', name: 'Focus' }
];

export function GameProvider({ children }) {
  const [player, setPlayer] = useState(DEFAULT_PLAYER);
  const [intentions, setIntentions] = useState([]);
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [toolCategories, setToolCategories] = useState(DEFAULT_TOOL_CATEGORIES);
  const [playbook, setPlaybook] = useState({});
  const [coachAdvice, setCoachAdvice] = useState(null);
  const [coachAdviceFollowed, setCoachAdviceFollowed] = useState([]);
  const [celebration, setCelebration] = useState(null);
  const [levelUp, setLevelUp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setUserId(session.user.id);
    });
  }, []);

  const showNotification = (msg, type = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  const triggerCelebration = (xpGain = 1) => {
    setCelebration(xpGain);
    setTimeout(() => setCelebration(null), 2000);
  };

  const triggerLevelUp = (level) => {
    setLevelUp(level);
    setTimeout(() => setLevelUp(null), 4000);
  };

  const api = useCallback((action, id) => {
    const params = new URLSearchParams({ uid: userId, ...(action && { action }), ...(id && { id }) });
    return `/api/game?${params}`;
  }, [userId]);

  const fetchAll = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await axios.get(`/api/game?uid=${userId}`);
      setPlayer(data.player || DEFAULT_PLAYER);
      setIntentions(data.intentions || []);
      setTools(data.tools || []);
      setCategories(data.categories || DEFAULT_CATEGORIES);
      setToolCategories(data.toolCategories || DEFAULT_TOOL_CATEGORIES);
      setPlaybook(data.playbook || {});
      setCoachAdvice(data.coachAdvice || null);
      setCoachAdviceFollowed(data.coachAdviceFollowed || []);
    } catch (e) {
      console.error('Backend unavailable, using local state');
      setPlayer(DEFAULT_PLAYER);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchAll();
      axios.post(api('checkdaily')).then(({ data }) => {
        if (data.message) showNotification(data.message, 'penalty');
      }).catch(() => {});
    }
  }, [userId, fetchAll, api]);

  const createIntention = async (text, category, xpMultiplier = 1) => {
    try {
      const { data } = await axios.post(api('intentions'), { text, category, xpMultiplier });
      setIntentions(prev => [...prev, data]);
      return data;
    } catch (e) {
      const local = { id: Date.now(), text, category, completed: false, xpMultiplier };
      setIntentions(prev => [...prev, local]);
      return local;
    }
  };

  const completeIntention = async (id) => {
    try {
      const { data } = await axios.post(api('complete', id));
      setIntentions(prev => prev.map(i => i.id === id ? data.intention : i));
      setPlayer(data.player);
      triggerCelebration(data.xpGain);
      if (data.leveledUp) {
        triggerLevelUp(data.player.level);
        showNotification(`LEVEL UP! You are now Level ${data.player.level}!`, 'success');
      } else if (data.hpGained > 0) {
        showNotification(`+${data.xpGain} XP! +${data.hpGained} HP earned!`, 'success');
      } else {
        showNotification(`+${data.xpGain} XP! Keep going!`, 'success');
      }
      return data;
    } catch (e) {
      setIntentions(prev => prev.map(i => i.id === id ? { ...i, completed: true } : i));
      setPlayer(prev => ({ ...prev, xp: prev.xp + 1 }));
      triggerCelebration(1);
      showNotification('+1 XP! Keep going!', 'success');
    }
  };

  const moveIntention = async (id, category) => {
    try {
      const { data } = await axios.post(api('move', id), { category });
      setIntentions(prev => prev.map(i => i.id === id ? data : i));
    } catch (e) {
      setIntentions(prev => prev.map(i => i.id === id ? { ...i, category } : i));
    }
  };

  const deleteIntention = async (id) => {
    try { await axios.post(api('deleteIntention', id)); } catch (e) {}
    setIntentions(prev => prev.filter(i => i.id !== id));
  };

  const updatePlayer = async (updates) => {
    if (updates.xpToLevelUp && updates.xpToLevelUp < 5) {
      showNotification('XP threshold must be at least 5!', 'error');
      return;
    }
    try {
      const { data } = await axios.post(api('player'), updates);
      setPlayer(data);
    } catch (e) {
      setPlayer(prev => ({ ...prev, ...updates }));
    }
  };

  const getCoachAdvice = async () => {
    const { data } = await axios.post(api('coach'));
    setCoachAdvice(data);
    return data;
  };

  const followCoachAdvice = async (category) => {
    const { data } = await axios.post(api('followcoach'), { category });
    setCoachAdviceFollowed(data.followed);
  };

  const addTool = async (name, url, category) => {
    try {
      const { data } = await axios.post(api('tools'), { name, url, category });
      setTools(prev => [...prev, data]);
    } catch (e) {
      setTools(prev => [...prev, { id: Date.now(), name, url, category }]);
    }
  };

  const removeTool = async (id) => {
    try { await axios.post(api('deleteTool', id)); } catch (e) {}
    setTools(prev => prev.filter(t => t.id !== id));
  };

  const addCategory = async (name, color) => {
    const newCat = { id: Date.now().toString(), name, icon: name.substring(0, 2), color: color || '#00ff88' };
    const updated = [...categories, newCat];
    setCategories(updated);
    try { await axios.post(api('categories'), { categories: updated }); } catch (e) {}
    return newCat;
  };

  const renameCategory = async (id, newName) => {
    const cat = categories.find(c => c.id === id);
    const updated = categories.map(c => c.id === id ? { ...c, name: newName } : c);
    setCategories(updated);
    if (cat) setIntentions(prev => prev.map(i => i.category === cat.name ? { ...i, category: newName } : i));
    try { await axios.post(api('categories'), { categories: updated }); } catch (e) {}
  };

  const deleteCategory = async (id) => {
    const cat = categories.find(c => c.id === id);
    const updated = categories.filter(c => c.id !== id);
    setCategories(updated);
    if (cat) setIntentions(prev => prev.filter(i => i.category !== cat.name));
    try { await axios.post(api('categories'), { categories: updated }); } catch (e) {}
  };

  const addToolCategory = async (name) => {
    const newCat = { id: Date.now().toString(), name };
    const updated = [...toolCategories, newCat];
    setToolCategories(updated);
    try { await axios.post(api('toolcategories'), { toolCategories: updated }); } catch (e) {}
  };

  const renameToolCategory = async (id, newName) => {
    const cat = toolCategories.find(c => c.id === id);
    const updated = toolCategories.map(c => c.id === id ? { ...c, name: newName } : c);
    setToolCategories(updated);
    if (cat) setTools(prev => prev.map(t => t.category === cat.name ? { ...t, category: newName } : t));
    try { await axios.post(api('toolcategories'), { toolCategories: updated }); } catch (e) {}
  };

  const deleteToolCategory = async (id) => {
    const cat = toolCategories.find(c => c.id === id);
    const updated = toolCategories.filter(c => c.id !== id);
    setToolCategories(updated);
    if (cat) setTools(prev => prev.filter(t => t.category !== cat.name));
    try { await axios.post(api('toolcategories'), { toolCategories: updated }); } catch (e) {}
  };

  const updatePlaybook = async (catName, section) => {
    const updated = { ...playbook, [catName]: section };
    setPlaybook(updated);
    try { await axios.post(api('playbook'), { playbook: updated }); } catch (e) {}
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <GameContext.Provider value={{
      player, intentions, tools, categories, toolCategories, playbook,
      coachAdvice, coachAdviceFollowed, celebration, levelUp, loading, notification, userId,
      createIntention, completeIntention, moveIntention, deleteIntention,
      updatePlayer, getCoachAdvice, followCoachAdvice,
      addTool, removeTool,
      addCategory, renameCategory, deleteCategory,
      addToolCategory, renameToolCategory, deleteToolCategory,
      updatePlaybook, showNotification, fetchAll, signOut
    }}>
      {children}
    </GameContext.Provider>
  );
}

export const useGame = () => useContext(GameContext);
